import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { PickList } from 'primereact/picklist';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import {
    listarGruposActivos,
    crearGrupo,
    actualizarGrupo,
    anularGrupo,
    reemplazarUsuariosGrupo,
    obtenerUsuariosDisponibles,
    obtenerGrupoPorId,
    subirLogoGrupo,
    descargarLogoGrupo,
    eliminarLogoGrupo
} from '../../services/Siniestro/SiniestroGrupoService';

const MIMES_LOGO = ['image/png', 'image/jpeg', 'image/jpg'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const SiniestroGrupos = ({ funcVolver }) => {
    const toast = useRef(null);
    const fileInputLogoRef = useRef(null);

    const [cargando, setCargando] = useState(false);
    const [grupos, setGrupos] = useState([]);
    const [usuariosDisp, setUsuariosDisp] = useState([]);

    // Estado del dialog
    const [showDialog, setShowDialog] = useState(false);
    const [grupoEditando, setGrupoEditando] = useState(null); // null = nuevo, objeto = edicion
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [origen, setOrigen] = useState([]);
    const [destino, setDestino] = useState([]);
    const [guardando, setGuardando] = useState(false);

    // Estado del logo (solo aplica en edicion: necesitamos el grupoId)
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    const [logoTieneActual, setLogoTieneActual] = useState(false);
    const [logoSubiendo, setLogoSubiendo] = useState(false);

    const cargarTodo = async () => {
        try {
            setCargando(true);
            const [gs, us] = await Promise.all([listarGruposActivos(), obtenerUsuariosDisponibles()]);
            setGrupos(gs);
            setUsuariosDisp(us);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los grupos' });
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargarTodo();
    }, []);

    const limpiarPreviewLogo = () => {
        if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
        setLogoPreviewUrl(null);
        setLogoTieneActual(false);
    };

    const cargarPreviewLogo = async (siniestroGrupoId) => {
        try {
            const blob = await descargarLogoGrupo(siniestroGrupoId);
            if (!blob) {
                setLogoPreviewUrl(null);
                setLogoTieneActual(false);
                return;
            }
            setLogoPreviewUrl(URL.createObjectURL(blob));
            setLogoTieneActual(true);
        } catch (e) {
            setLogoPreviewUrl(null);
            setLogoTieneActual(false);
        }
    };

    const abrirNuevo = () => {
        limpiarPreviewLogo();
        setGrupoEditando(null);
        setNombre('');
        setDescripcion('');
        setOrigen(usuariosDisp);
        setDestino([]);
        setShowDialog(true);
    };

    const abrirEditar = async (grupo) => {
        try {
            setCargando(true);
            limpiarPreviewLogo();
            const detalle = await obtenerGrupoPorId(grupo.siniestroGrupoId);
            setGrupoEditando(detalle);
            setNombre(detalle.nombre || '');
            setDescripcion(detalle.descripcion || '');

            const idsAsignados = new Set((detalle.usuarios || []).map((u) => u.usuarioId));
            const dest = usuariosDisp.filter((u) => idsAsignados.has(u.usuarioId));
            const orig = usuariosDisp.filter((u) => !idsAsignados.has(u.usuarioId));
            setOrigen(orig);
            setDestino(dest);
            if (detalle.tieneLogo) {
                cargarPreviewLogo(detalle.siniestroGrupoId);
            }
            setShowDialog(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo abrir el grupo' });
        } finally {
            setCargando(false);
        }
    };

    const cerrarDialog = () => {
        if (guardando || logoSubiendo) return;
        setShowDialog(false);
        setGrupoEditando(null);
        limpiarPreviewLogo();
    };

    const onSeleccionLogo = async (e) => {
        const archivo = e.target.files?.[0];
        e.target.value = ''; // permite re-seleccionar el mismo archivo
        if (!archivo) return;
        if (!grupoEditando?.siniestroGrupoId) {
            toast.current?.show({ severity: 'warn', summary: 'Atencion', detail: 'Primero guarde el grupo, luego cargue el logo.' });
            return;
        }
        if (!MIMES_LOGO.includes(archivo.type)) {
            toast.current?.show({ severity: 'warn', summary: 'Formato no permitido', detail: 'Solo PNG o JPG.' });
            return;
        }
        if (archivo.size > MAX_LOGO_BYTES) {
            toast.current?.show({ severity: 'warn', summary: 'Muy grande', detail: 'El logo no puede superar los 2 MB.' });
            return;
        }
        try {
            setLogoSubiendo(true);
            await subirLogoGrupo(grupoEditando.siniestroGrupoId, archivo);
            await cargarPreviewLogo(grupoEditando.siniestroGrupoId);
            toast.current?.show({ severity: 'success', summary: 'Listo', detail: 'Logo cargado' });
        } catch (error) {
            const detalle = error?.response?.data && typeof error.response.data === 'string'
                ? error.response.data
                : 'No se pudo subir el logo';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: detalle });
        } finally {
            setLogoSubiendo(false);
        }
    };

    const eliminarLogo = () => {
        if (!grupoEditando?.siniestroGrupoId) return;
        confirmDialog({
            message: 'Eliminar el logo del grupo?',
            header: 'Confirmar',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Si, eliminar',
            rejectLabel: 'Cancelar',
            accept: async () => {
                try {
                    setLogoSubiendo(true);
                    await eliminarLogoGrupo(grupoEditando.siniestroGrupoId);
                    limpiarPreviewLogo();
                    toast.current?.show({ severity: 'success', summary: 'Listo', detail: 'Logo eliminado' });
                } catch (error) {
                    const detalle = error?.response?.data && typeof error.response.data === 'string'
                        ? error.response.data
                        : 'No se pudo eliminar el logo';
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: detalle });
                } finally {
                    setLogoSubiendo(false);
                }
            }
        });
    };

    const onPickListChange = (event) => {
        setOrigen(event.source);
        setDestino(event.target);
    };

    const itemTemplate = (item) => (
        <div className="flex flex-wrap p-2 align-items-center">
            <div>
                <div className="font-bold">{item.username}</div>
                <div className="text-sm text-color-secondary">{item.nombre}</div>
            </div>
        </div>
    );

    const guardar = async () => {
        if (!nombre || !nombre.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Atencion', detail: 'El nombre es obligatorio' });
            return;
        }
        try {
            setGuardando(true);
            const usuariosIds = destino.map((u) => u.usuarioId);

            if (!grupoEditando) {
                // Crear
                await crearGrupo({ nombre: nombre.trim(), descripcion, usuariosIds });
                toast.current?.show({ severity: 'success', summary: 'Listo', detail: 'Grupo creado' });
            } else {
                // Actualizar nombre y descripcion
                await actualizarGrupo({
                    siniestroGrupoId: grupoEditando.siniestroGrupoId,
                    nombre: nombre.trim(),
                    descripcion
                });
                // Reemplazar usuarios
                await reemplazarUsuariosGrupo(grupoEditando.siniestroGrupoId, usuariosIds);
                toast.current?.show({ severity: 'success', summary: 'Listo', detail: 'Grupo actualizado' });
            }
            setShowDialog(false);
            setGrupoEditando(null);
            cargarTodo();
        } catch (error) {
            const detalle = error?.response?.data && typeof error.response.data === 'string'
                ? error.response.data
                : 'No se pudo guardar el grupo';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: detalle });
        } finally {
            setGuardando(false);
        }
    };

    const eliminar = (grupo) => {
        confirmDialog({
            message: `Seguro que desea eliminar el grupo "${grupo.nombre}"?`,
            header: 'Confirmar',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Si, eliminar',
            rejectLabel: 'Cancelar',
            accept: async () => {
                try {
                    await anularGrupo(grupo.siniestroGrupoId);
                    toast.current?.show({ severity: 'success', summary: 'Eliminado', detail: 'Grupo eliminado' });
                    cargarTodo();
                } catch (error) {
                    const detalle = error?.response?.data && typeof error.response.data === 'string'
                        ? error.response.data
                        : 'No se pudo eliminar';
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: detalle });
                }
            }
        });
    };

    const accionesTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" onClick={() => abrirEditar(rowData)} tooltip="Editar" />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger" onClick={() => eliminar(rowData)} tooltip="Eliminar" />
        </div>
    );

    const logoColTemplate = (rowData) => {
        if (!rowData?.tieneLogo) return <span className="text-color-secondary">—</span>;
        return <i className="pi pi-image" style={{ color: '#1565C0', fontSize: '1.2rem' }} title="Tiene logo cargado" />;
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-between align-items-center mb-3">
                <h4 className="m-0">Grupos de Usuarios — Siniestros</h4>
                <div className="flex gap-2">
                    <Button label="Nuevo Grupo" icon="pi pi-plus" onClick={abrirNuevo} />
                    {funcVolver && <Button label="Volver" icon="pi pi-arrow-left" className="p-button-secondary" onClick={funcVolver} />}
                </div>
            </div>

            {cargando ? (
                <div className="flex justify-content-center my-5">
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                </div>
            ) : (
                <DataTable
                    value={grupos}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    emptyMessage="No hay grupos creados"
                    stripedRows
                    size="small"
                >
                    <Column field="nombre" header="Nombre" sortable />
                    <Column field="descripcion" header="Descripción" />
                    <Column field="cantidadUsuarios" header="# Usuarios" style={{ width: '120px', textAlign: 'center' }} />
                    <Column body={logoColTemplate} header="Logo" style={{ width: '80px', textAlign: 'center' }} />
                    <Column body={accionesTemplate} header="Acciones" style={{ width: '140px' }} />
                </DataTable>
            )}

            <Dialog
                header={grupoEditando ? 'Editar Grupo' : 'Nuevo Grupo'}
                visible={showDialog}
                onHide={cerrarDialog}
                style={{ width: '70vw' }}
                modal
            >
                <div className="formgrid grid p-fluid">
                    <div className="field col-12 md:col-6">
                        <label htmlFor="nombre">Nombre *</label>
                        <InputText id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={120} />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="descripcion">Descripción</label>
                        <InputTextarea id="descripcion" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} maxLength={255} />
                    </div>
                    <div className="field col-12">
                        <label className="font-semibold">Usuarios del Grupo</label>
                        <PickList
                            source={origen}
                            target={destino}
                            onChange={onPickListChange}
                            itemTemplate={itemTemplate}
                            sourceHeader="Disponibles"
                            targetHeader="Asignados"
                            sourceStyle={{ height: '300px' }}
                            targetStyle={{ height: '300px' }}
                            filter
                            filterBy="username,nombre"
                        />
                    </div>

                    <div className="field col-12">
                        <label className="font-semibold">Logo de la Empresa</label>
                        {!grupoEditando ? (
                            <div className="text-color-secondary text-sm">
                                Primero guarde el grupo y vuelva a abrirlo en edición para cargar su logo.
                            </div>
                        ) : (
                            <div className="flex align-items-center gap-3 mt-2">
                                <div
                                    className="flex align-items-center justify-content-center border-1 border-300 border-round"
                                    style={{ width: '120px', height: '80px', background: '#f8fafc' }}
                                >
                                    {logoPreviewUrl ? (
                                        <img
                                            src={logoPreviewUrl}
                                            alt="Logo"
                                            style={{ maxHeight: '72px', maxWidth: '112px', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <span className="text-color-secondary text-sm">Sin logo</span>
                                    )}
                                </div>
                                <div className="flex flex-column gap-2">
                                    <Button
                                        label={logoTieneActual ? 'Cambiar logo' : 'Subir logo'}
                                        icon="pi pi-upload"
                                        className="p-button-sm"
                                        loading={logoSubiendo}
                                        onClick={() => fileInputLogoRef.current?.click()}
                                    />
                                    {logoTieneActual && (
                                        <Button
                                            label="Eliminar"
                                            icon="pi pi-trash"
                                            className="p-button-sm p-button-danger p-button-text"
                                            disabled={logoSubiendo}
                                            onClick={eliminarLogo}
                                        />
                                    )}
                                </div>
                                <div className="text-sm text-color-secondary">
                                    PNG o JPG, máx 2 MB. Se mostrará en los reportes PDF y en el header de las pantallas del módulo cuando el usuario pertenezca a un único grupo.
                                </div>
                                <input
                                    ref={fileInputLogoRef}
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    style={{ display: 'none' }}
                                    onChange={onSeleccionLogo}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-content-end gap-2 mt-3">
                    <Button label="Cancelar" icon="pi pi-times" className="p-button-secondary" onClick={cerrarDialog} disabled={guardando} />
                    <Button label="Guardar" icon="pi pi-save" onClick={guardar} loading={guardando} />
                </div>
            </Dialog>
        </div>
    );
};

export default SiniestroGrupos;
